import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Title from "./TabTitle";
import ChitPosting from "../../pages/ChitPosting/ChitPosting";
import { useNavigate, useMatch } from "react-router-dom";
import { addTab, removeTab, useTabs, TAB_TYPES } from "slices/tabsSlice";
import PaymentProcessing from "pages/PaymentProcessing";
import { Tabs } from "components/Enums/TabEnums.ts";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import AccessRevokedModal from "components/common/AccessRevokedModal";
import BillRequests from "pages/BillRequests/BillRequests";
import TransactionSearch from "pages/TransactionSearch/TransactionSearch";

const AdminTabs = React.memo((props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [selected, setSelected] = useState(-1);

  const tabsData = useTabs(TAB_TYPES.ADMIN);
  const { allowed } = useAllowedLiscence(Tabs.ADMIN);

  const findContent = (item) => {
    switch (item) {
      case "billrequests":
        return "Bill Requests";
      case "chitposting":
        return "Chit Posting";
      case "paymentprocessing":
        return "Payment Processing";
      case "transactionsearch":
        return "Transaction Search";
      default:
        break;
    }
  };

  const urlDefiner = (e) => {
    if (params !== null) {
      let url = tabs[e]?.toLowerCase();
      if (url === undefined) {
        url = "";
      }
      navigate(`/admin/${url}`);
    }
  };

  const params = useMatch("admin/:data");

  useEffect(() => {
    if (params !== null) {
      let data = params?.params?.data;
      const dataLower = data?.toLowerCase();
      if (!tabsData.includes(dataLower)) {
        dispatch(addTab("admin", dataLower));
      } else {
        setSelected(tabsData.indexOf(dataLower));
      }
    }
  }, [params, tabsData, dispatch]);

  useEffect(() => {
    if (tabsData) {
      setTabs(tabsData);
      const segment = params?.params?.data?.toLowerCase();
      const index = segment ? tabsData.indexOf(segment) : -1;
      setSelected(index === -1 ? 0 : index);
    }
  }, [tabsData, params?.params?.data]);

  const removeTabDispatcher = (tab) => {
    const closedIndex = tabs.findIndex((t) => findContent(t) === tab);
    if (closedIndex === -1) return;

    const closedTabKey = tabs[closedIndex];
    const dispatchRemove = () => {
      queueMicrotask(() => {
        dispatch(removeTab("admin", closedTabKey));
      });
    };

    const newTabs = tabs.filter((t) => findContent(t) !== tab);
    if (!newTabs.length) {
      navigate("/");
      setSelected(0);
      dispatchRemove();
      return;
    }
    const newSelected =
      closedIndex === selected
        ? Math.min(selected, newTabs.length - 1)
        : closedIndex < selected
          ? selected - 1
          : selected;
    setSelected(newSelected);
    navigate(`/admin/${newTabs[newSelected]?.toLowerCase() ?? ""}`);
    dispatchRemove();
  };

  const handleSelect = (e) => {
    setSelected(e.selected);
    urlDefiner(e.selected);
  };

  return allowed ? (
    <TabStrip
      selected={selected}
      onSelect={handleSelect}
      keepTabsMounted={true}
      className="navigationTabs h-100">
      {tabs.map((item) => {
        return (
          item && (
            <TabStripTab
              title={
                <Title
                  content={findContent(item)}
                  onTabRemove={removeTabDispatcher}
                  selected={findContent(tabs.at(selected))}
                  count={tabs.length}
                />
              }
              key={item}
              contentClassName="h-100">
              {item === "chitposting" ? <ChitPosting /> : ""}
              {item === "billrequests" ? <BillRequests /> : ""}
              {item === "paymentprocessing" && <PaymentProcessing />}
              {item === "transactionsearch" && <TransactionSearch />}
            </TabStripTab>
          )
        );
      })}
    </TabStrip>
  ) : (
    <AccessRevokedModal removeTab={() => props.closeTab()} />
  );
});

export default AdminTabs;
